import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  fetchAdminApplicationNotes,
  createAdminApplicationNote,
} from "../../../api/adminData";
import { formatDateTime } from "../../../utils/datetime";

export default function ApplicationNotesBlock({ applicationId }) {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");

  const loadNotes = useCallback(async () => {
    if (!applicationId) return;
    setLoadError("");
    try {
      const data = await fetchAdminApplicationNotes(applicationId);
      setNotes(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setLoadError(e?.message || "Notizen konnten nicht geladen werden.");
      setNotes([]);
    }
  }, [applicationId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setSaving(true);
    setError("");
    try {
      await createAdminApplicationNote(applicationId, draft.trim());
      await loadNotes();
      setDraft("");
      toast.success("Notiz gespeichert.");
    } catch (err) {
      setError(err?.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-4 rounded-[14px] border border-black/10 bg-white p-6 dark:border-white/[0.07] dark:bg-[#141824]">
      <h3 className="mb-4 mt-0 text-[9px] font-bold uppercase tracking-[1px] text-slate-500 dark:text-slate-400">
        Notizen
      </h3>

      <form onSubmit={handleSubmit}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={saving}
          placeholder="Interne Notiz …"
          className="min-h-[80px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-[#FF7A3D] dark:border-white/10 dark:bg-[#1a2030] dark:text-white"
        />
        {error && (
          <div className="mt-2 text-xs text-red-600 dark:text-red-400">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={saving || !draft.trim()}
          className="mt-3 rounded-md border border-[#FF7A3D] bg-[#FF7A3D] px-4 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60"
        >
          {saving ? "Speichere…" : "Notiz speichern"}
        </button>
      </form>

      <div className="mt-4 border-t border-black/10 dark:border-white/[0.07] pt-4">
        {loadError ? (
          <div className="text-xs text-red-600 dark:text-red-400">{loadError}</div>
        ) : notes.length === 0 ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Noch keine Notizen
          </div>
        ) : (
          <ul className="m-0 list-none space-y-3 p-0">
            {notes.map((n) => (
              <li key={n.id}>
                <div className="whitespace-pre-wrap text-sm text-slate-800 dark:text-white">
                  {n.content}
                </div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {formatDateTime(n.created_at)} · {n.author_name || "—"}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
